import { useState, useEffect, type ChangeEvent } from 'react';
import Modal from '@modalSrc/Modal';
import { toast } from 'react-toastify';
import RightTimeApi from '@/API';
import '@/style/sass/_companyFeeItemModal.scss';

type CompanyFeeItem = {
    id?: number;
    itemName?: string;
    amount?: number | string;
    active?: boolean;
    feeScheduleItemName?: string;
    feeScheduleName?: string;
};

type CompanyModel = {
    id: number | string;
};

type CompanyFeeItemModalProps = {
    isOpen: boolean;
    onClose: () => void;
    editingItem?: CompanyFeeItem | null;
    company: CompanyModel;
    onSave?: () => void;
};

export default function CompanyFeeItemModal({ isOpen, onClose, editingItem, company, onSave }: CompanyFeeItemModalProps) {
    const [name, setName] = useState('');
    const [rate, setRate] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Reset form when modal opens/closes or editing item changes
    useEffect(() => {
        if (isOpen) {
            if (editingItem) {
                setName(editingItem.itemName || '');
                setRate(editingItem.amount != null ? String(editingItem.amount) : '');
            } else {
                setName('');
                setRate('');
            }
        }
    }, [isOpen, editingItem]);

    const handleSave = async () => {
        if (!name.trim()) {
            toast.error('Item name is required');
            return;
        }

        const parsedRate = Number(rate);
        if (!rate || Number.isNaN(parsedRate) || parsedRate < 0) {
            toast.error('Please enter a valid rate');
            return;
        }

        const itemData = {
            id: editingItem ? editingItem.id : 0,
            companyId: company.id,
            itemName: name.trim(),
            amount: parsedRate
        };

        setIsLoading(true);
        try {
            let response = await RightTimeApi.sendAsync(`CompanyFeeItem`, itemData);
            if (response && response.ok) {
                toast.success(`Fee item ${editingItem ? 'updated' : 'created'} successfully`);
                onSave && onSave();
                onClose();
            } else {
                toast.error(`Failed to ${editingItem ? 'update' : 'create'} fee item`);
            }
        } catch (error) {
            console.error('Error saving fee item:', error);
            const message = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Error ${editingItem ? 'updating' : 'creating'} fee item: ${message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const footer = (
        <>
            <div className="footerLeft">
                {/* Left side footer content if needed */}
            </div>
            <div className="footerRight">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isLoading}
                >
                    {isLoading ? 'Saving...' : 'Save'}
                </button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={isLoading}
                >
                    Cancel
                </button>
            </div>
        </>
    );

    return (
        <Modal
            isOpen={isOpen}
            title={editingItem ? 'Edit Fee Item' : 'Add Fee Item'}
            onClose={onClose}
            footer={footer}
            className="companyFeeItemModal"
        >
            <div className="formGroup">
                <label htmlFor="itemName">Item Name:</label>
                <input
                    type="text"
                    id="itemName"
                    className="form-control"
                    value={name}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                    placeholder="Enter item name"
                    disabled={isLoading}
                />
            </div>

            <div className="formGroup">
                <label htmlFor="itemRate">Rate:</label>
                <input
                    type="number"
                    id="itemRate"
                    className="form-control"
                    value={rate}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setRate(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={isLoading}
                />
            </div>

            {editingItem && (
                <>
                    <div className="formGroup">
                        <label>Active:</label>
                        <div className="form-control-static">
                            {editingItem.active ? 'Yes' : 'No'}
                        </div>
                    </div>

                    <div className="formGroup">
                        <label>Fee Schedule Item Name:</label>
                        <div className="form-control-static">
                            {editingItem.feeScheduleItemName || 'N/A'}
                        </div>
                    </div>

                    <div className="formGroup">
                        <label>Fee Schedule Name:</label>
                        <div className="form-control-static">
                            {editingItem.feeScheduleName || 'N/A'}
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
}

